import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Copy, Trash2, History, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, errorText } from "@/lib/api";
import { Panel } from "@/components/admin/shared";
import { CodeEditor } from "@/components/admin/codes/CodeEditor";
import { PAGE_OPTIONS } from "@/components/decor/constants";

const pageNames = (pages) => pages.map((p) => Object.fromEntries(PAGE_OPTIONS)[p]).join(", ");

const Versions = ({ b, onRestore }) => {
  const [v, setV] = useState(null);
  useEffect(() => { api.get(`/admin/code-blocks/${b.id}/versions`).then((r) => setV(r.data)); }, [b.id, b.updated_at]);
  if (!v) return <Loader2 className="animate-spin" size={14} />;
  return (
    <ul className="mt-2 space-y-1 text-xs" data-testid={`code-versions-${b.id}`}>
      {v.map((x) => (
        <li key={x.id} className="flex items-center gap-2">
          <span className="text-mute">{new Date(x.at).toLocaleString("es-MX")}</span><span className="font-semibold">{x.action}</span><span className="text-mute2">{x.by}</span>
          {x.snapshot && <button className="ml-auto text-gold" onClick={() => onRestore(x.id)} data-testid={`code-restore-${x.id}`}>Restaurar</button>}
        </li>
      ))}
    </ul>
  );
};

export const CodesTab = () => {
  const [blocks, setBlocks] = useState(null);
  const [editing, setEditing] = useState(null);
  const [hist, setHist] = useState(null);
  const load = useCallback(() => api.get("/admin/code-blocks").then((r) => setBlocks(r.data)).catch((e) => toast.error(errorText(e))), []);
  useEffect(() => { load(); }, [load]);
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errorText(e)); } };
  if (!blocks) return <Loader2 className="animate-spin text-brand" />;
  if (editing) return <CodeEditor block={editing === "new" ? null : editing} onClose={() => { setEditing(null); load(); }} />;
  return (
    <Panel title="Administrador de códigos y efectos web" action={<button className="btn-gold !px-3 !py-2 text-xs" onClick={() => setEditing("new")} data-testid="code-new-btn"><Plus size={14} /> Nuevo código</button>}>
      <p className="mb-4 text-sm text-mute">Agrega HTML, CSS o SVG (se limpian solos antes de publicarse) o JavaScript que corre en un recuadro aislado y no puede leer sesiones ni datos de clientes. Las decoraciones de temporada están en su propia pestaña.</p>
      {!blocks.length && <p className="text-sm text-mute">Aún no hay códigos.</p>}
      <ul className="divide-y divide-line">
        {blocks.map((b) => (
          <li key={b.id} className="py-3" data-testid={`code-item-${b.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <Switch checked={b.active} onCheckedChange={(v) => run(() => api.post(`/admin/code-blocks/${b.id}/active`, { active: v }), v ? "Activado" : "Desactivado")} data-testid={`code-switch-${b.id}`} />
              <div className="min-w-0 flex-1">
                <p className="font-bold">{b.name} <span className="ml-1 rounded-full bg-surface px-2 py-0.5 text-[10px] uppercase text-mute">{b.kind === "js" ? "JavaScript aislado" : "HTML/CSS/SVG"}</span></p>
                <p className="text-xs text-mute">{pageNames(b.pages)} · {b.position === "top" ? "arriba del contenido" : "abajo del contenido"}{b.start && ` · desde ${b.start.replace("T", " ")}`}{b.end && ` · hasta ${b.end.replace("T", " ")}`} · {b.showing_now ? "se muestra ahora" : "no se muestra"}</p>
              </div>
              <button aria-label="Editar" onClick={() => setEditing(b)} data-testid={`code-edit-${b.id}`}><Pencil size={16} /></button>
              <button aria-label="Duplicar" onClick={() => run(() => api.post(`/admin/code-blocks/${b.id}/duplicate`), "Duplicado")} data-testid={`code-duplicate-${b.id}`}><Copy size={16} /></button>
              <button aria-label="Historial" onClick={() => setHist(hist === b.id ? null : b.id)} data-testid={`code-history-${b.id}`}><History size={16} /></button>
              <button aria-label="Eliminar" className="text-red-600" onClick={() => window.confirm(`¿Eliminar «${b.name}»?`) && run(() => api.delete(`/admin/code-blocks/${b.id}`), "Eliminado")} data-testid={`code-delete-${b.id}`}><Trash2 size={16} /></button>
            </div>
            {hist === b.id && <Versions b={b} onRestore={(vid) => run(() => api.post(`/admin/code-blocks/${b.id}/restore/${vid}`), "Versión restaurada")} />}
          </li>
        ))}
      </ul>
    </Panel>
  );
};
